import invariant from 'invariant';
import { isNumber } from 'lodash';
import type {
  Asset,
  Folder,
  Project,
  Share,
} from '@frameio/graphql-client/generated';
// @todo (WEB-1045): Add condensed UUID support to routes
// import { toCondensedUUID } from '@frameio/utils/formatters/uuid';
import { getPathnameFromHref } from '../helpers/getPathnameFromHref';
import type { ValidHref } from '../types';

// @todo (WEB-1045): Remove when condensed UUID support is added to routes
const toCondensedUUID = (uuid: string): string => uuid;

/**
 * ShareBuilderPage and cooresponding Viewer Page
 *
 * /project/:projectId/share-builder/:shareId(/:folderId)(/view/:assetId)
 */
type ShareBuilderPage =
  | {
      page: 'shareBuilder';
      projectId: Project['id'];
      shareId: Share['id'];
      folderId?: Folder['id'];
      assetId?: Asset['id'];
      commentId?: string;
      versionId?: string;
      compare?: string[];
      activeIndex?: number;
    }
  | {
      page: 'shareBuilderViewer';
      projectId: Project['id'];
      shareId: Share['id'];
      folderId?: Folder['id'];
      assetId: Asset['id'];
      commentId?: string;
      versionId?: string;
      compare?: string[];
      activeIndex?: number;
    };

export type ShareBuilderGetLinkParams = ShareBuilderPage;

export const getLinkForShareBuilder = (
  params: ShareBuilderGetLinkParams
): ValidHref => {
  // Assert required params for better error messages
  invariant(params.projectId, 'projectId is required');
  const projectId = toCondensedUUID(params.projectId);
  invariant(params.shareId, 'shareId is required');
  const shareId = toCondensedUUID(params.shareId);
  const folderId = params.folderId
    ? toCondensedUUID(params.folderId)
    : undefined;

  const assetId = params.assetId ? toCondensedUUID(params.assetId) : undefined;

  const urlSearchParams = new URLSearchParams();
  let newUrl = `/project/${projectId}/share-builder/${
    folderId ? `${shareId}/${folderId}` : shareId
  }${assetId ? `/view/${assetId}` : ''}`;

  // ... ?c=:commentId
  if ('commentId' in params && params.commentId) {
    urlSearchParams.set('c', params.commentId);
  }

  // ... ?versionId=:versionId
  if ('versionId' in params && params.versionId) {
    urlSearchParams.set('version', params.versionId);
  }

  // ... ?activeIndex=:activeIndex
  if ('activeIndex' in params && isNumber(params.activeIndex)) {
    urlSearchParams.set('activeIndex', params.activeIndex.toString());
  }

  // ... ?compare=:comparisonId1&compare=comparisonId2
  if ('compare' in params && params.compare) {
    params.compare.forEach((param) => {
      urlSearchParams.append('compare', toCondensedUUID(param));
    });
  }

  if (urlSearchParams.toString()) {
    newUrl += `?${
      urlSearchParams.toString() as {
        c?: string;
        version?: string;
        compare?: string;
        activeIndex?: string;
      }
    }`;
  }
  return newUrl as ValidHref;
};

export const isShareBuilderPageType = (href: ValidHref): boolean => {
  const pathname = getPathnameFromHref(href);
  const parts = pathname.split('/');

  // /project/:projectId/share-builder/:shareId(/:folderId)(/view/:assetId)
  return parts[1] === 'project' && parts[3] === 'share-builder';
};

export const isShareBuilderViewerPageType = (
  href: ValidHref | string
): boolean => {
  const pathname = getPathnameFromHref(href);
  const parts = pathname.split('/');
  return (
    parts[1] === 'project' &&
    parts[3] === 'share-builder' &&
    parts.includes('view')
  );
};
